import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import { PartyPopper } from 'lucide-react';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(5);

  const token = searchParams.get('token');

  useEffect(() => {
    // Si déjà connecté, rediriger
    if (user) {
      navigate('/');
      return;
    }

    if (!token) {
      setStatus('error');
      setMessage('Lien de vérification invalide. Aucun token fourni.');
      return;
    }

    // Vérifier le token
    const verifyEmail = async () => {
      try {
        const response = await api.post('/auth/verify-email', { token });
        
        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message);
          
          // Sauvegarder le token de connexion
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
          }
          
          // Redirection automatique après 5 secondes
          const interval = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(interval);
                window.location.href = '/';
              }
              return prev - 1;
            });
          }, 1000);
        }
      } catch (error) {
        const errorData = error.response?.data;
        
        if (errorData?.expired) {
          setStatus('expired');
          setMessage('Ce lien de vérification a expiré.');
        } else {
          setStatus('error');
          setMessage(errorData?.error || 'Une erreur est survenue lors de la vérification.');
        }
      }
    };

    verifyEmail();
  }, [token, user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          
          {/* Loading */}
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-indigo-100 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Vérification en cours...</h1>
              <p className="text-gray-600">Veuillez patienter pendant que nous vérifions votre email.</p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-2">Email vérifié ! <PartyPopper className="w-6 h-6 text-yellow-500" /></h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <p className="text-sm text-gray-500 mb-4">
                Redirection automatique dans <span className="font-bold text-indigo-600">{countdown}</span> secondes...
              </p>
              <Link 
                to="/pourtoi"
                className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Accéder à mon compte
              </Link>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Échec de la vérification</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <Link 
                to="/login"
                className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Retour à la connexion
              </Link>
            </>
          )}

          {/* Expired */}
          {status === 'expired' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg className="w-10 h-10 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Lien expiré</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <p className="text-sm text-gray-500 mb-4">
                Vous pouvez demander un nouveau lien de vérification depuis la page de connexion.
              </p>
              <Link 
                to="/login"
                className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Retour à la connexion
              </Link>
            </>
          )}

        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          © {new Date().getFullYear()} DJAMANN - Tous droits réservés
        </p>
      </div>
    </div>
  );
};

export default VerifyEmail;
